import React from 'react'
import { Box, Typography, Radio } from '@material-ui/core'
import { makeStyles, ThemeProvider, createMuiTheme } from '@material-ui/core/styles'
import DiligentBrandIcon from 'assets/icons/DiligentBrandIcon'
import { truncateString } from 'common/util/functions'
import i18n from 'common/util/internationalization'
import {
    MINUTES_TEMPLATES_DILIGEN_ACADEMY_TEMPLATE_DESCRIPTION,
    MINUTES_TEMPLATES_DILIGENT_ACADEMY_TEMPLATE_TITLE,
    MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION,
    MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE
} from 'common/constants/constants'

interface TemplateCardProps {
    template: any
    onPreview: (templateId: string) => void
    onPreviewButton?: (templateId: string) => void
    withRadio?: boolean
    templateOption?: any
    previewSelection?: any
    setPreviewSelection?: (template: any) => void
    showPreview?: boolean
    isActionsOnly?: boolean
}

const useStyles = makeStyles(() => ({
    templateCard: {
        border: '1px solid #E6E6E6',
        borderRadius: 8,
        padding: '16px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: '16px',
        background: '#fff',
        position: 'relative',
        width: '100%'
    },
    logo: {
        marginRight: 16,
        display: 'flex',
        alignItems: 'center',
        width: 32,
        height: 32
    },
    templateCardContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },
    templateTitle: {
        fontSize: '16px',
        fontFamily: 'Source Sans Pro',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '20px',
        color: '#1E1E1E'
    },
    templateSubtitle: {
        fontSize: '14px',
        fontFamily: 'Source Sans Pro',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '18px',
        color: '#6B7280'
    },
    templateCardPreviewLink: {
        color: '#455D82',
        fontWeight: 600,
        fontSize: '14px',
        cursor: 'pointer',
        fontFamily: 'Source Sans Pro',
        '&:hover': {
            textDecoration: 'underline'
        },
        '&:focus': {
            outline: 'none',
            boxShadow: '0px 0px 0px 1px #ffffff, 0px 0px 0px 3px #455D82',
            borderRadius: '2px'
        }
    },
    defaultTag: {
        backgroundColor: '#E6E6E6',
        padding: '5px 10px',
        textAlign: 'center',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 400,
        color: '#1E1E1E',
        marginRight: '20px',
        marginLeft: '30px'
    },
    templateCardSelected: {
        outline: '1px solid #455D82',
        background: '#FFF',
        textAlign: 'left',
        borderRadius: '10px'
    }
}))

const TemplateCard: React.FC<TemplateCardProps> = ({
    template,
    onPreview,
    onPreviewButton,
    withRadio = false,
    templateOption,
    previewSelection,
    setPreviewSelection,
    showPreview = false,
    isActionsOnly = false
}) => {
    const classes = useStyles()
    const theme = createMuiTheme({
        overrides: {
            MuiRadio: {
                root: {
                    color: '#2F3B4D'
                },
                colorSecondary: {
                    '&.Mui-checked': {
                        color: '#2F3B4D'
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    },
                    '& .MuiSvgIcon-root': {
                        color: '#2F3B4D'
                    },
                    '&.Mui-checked .MuiSvgIcon-root': {
                        color: '#2F3B4D'
                    }
                }
            }
        }
    })

    const handlePreviewClick = () => {
        if (onPreviewButton) {
            onPreviewButton(template.id)
        } else {
            onPreview(template.id)
        }
    }

    const handleCardClick = () => {
        if (withRadio && setPreviewSelection) {
            setPreviewSelection(template)
            // Only call preview if there's no separate preview button handler (i.e., not iPad)
            if (!onPreviewButton) {
                onPreview(template.id)
            }
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            if (withRadio) {
                handleCardClick()
            } else {
                handlePreviewClick()
            }
        }
    }

    // If template is an array, iterate and render the same card for each object
    if (Array.isArray(template)) {
        return (
            <>
                {isActionsOnly && (
                    <Box
                        className={`${classes.templateCard} ${
                            withRadio ? classes.templateCardSelected : ''
                        }`}
                        onClick={handleCardClick}
                        style={{ cursor: withRadio ? 'pointer' : 'default' }}>
                        {/* Icon */}
                        {
                            <Box className={classes.logo}>
                                <DiligentBrandIcon />
                            </Box>
                        }
                        {/* Title and Description */}
                        <Box className={classes.templateCardContent}>
                            <Typography className={classes.templateTitle}>
                                {i18n.t('MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE_ACTIONS_ONLY')}
                            </Typography>
                            <Typography className={classes.templateSubtitle}>
                                {isActionsOnly
                                    ? i18n.t('MINUTES_TEMPLATES_ACTION_TEMPLATE_DESCRIPTION')
                                    : null}
                            </Typography>
                        </Box>
                    </Box>
                )}
                {!isActionsOnly && (
                    <Box style={{ width: '100%' }}>
                        {template.map((tpl) => (
                            <Box
                                key={tpl?.id}
                                className={`${classes.templateCard} ${
                                    withRadio &&
                                    (previewSelection?.id || templateOption?.id) === tpl?.id
                                        ? classes.templateCardSelected
                                        : ''
                                }`}
                                onClick={() => {
                                    if (withRadio && setPreviewSelection) {
                                        setPreviewSelection(tpl)
                                        if (!onPreviewButton) {
                                            onPreview(tpl.id)
                                        }
                                    }
                                }}
                                style={{ cursor: withRadio ? 'pointer' : 'default' }}>
                                {withRadio && (
                                    <ThemeProvider theme={theme}>
                                        <Radio
                                            value={tpl?.id}
                                            inputProps={{ 'aria-label': tpl?.title }}
                                            style={{ marginRight: 16 }}
                                            tabIndex={0}
                                            checked={
                                                (previewSelection?.id || templateOption?.id) ===
                                                tpl?.id
                                            }
                                            onChange={() => {
                                                setPreviewSelection?.(tpl)
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === ' ' || e.key === 'Enter') {
                                                    e.preventDefault()
                                                    setPreviewSelection?.(tpl)
                                                    onPreview(tpl?.id)
                                                }
                                            }}
                                        />
                                    </ThemeProvider>
                                )}

                                {/* Icon */}
                                {tpl?.site === null && (
                                    <Box className={classes.logo}>
                                        <DiligentBrandIcon />
                                    </Box>
                                )}
                                {/* Title and Description */}
                                <Box className={classes.templateCardContent}>
                                    <Typography className={classes.templateTitle}>
                                        {tpl?.title === MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE
                                            ? i18n.t('MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE')
                                            : tpl?.title ===
                                              MINUTES_TEMPLATES_DILIGENT_ACADEMY_TEMPLATE_TITLE
                                            ? i18n.t(
                                                  'MINUTES_TEMPLATES_DILIGENT_ACADEMY_TEMPLATE_TITLE'
                                              )
                                            : null}
                                    </Typography>
                                    {tpl?.description && (
                                        <Typography className={classes.templateSubtitle}>
                                            {tpl?.description ===
                                            MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION
                                                ? i18n.t(
                                                      'MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION'
                                                  )
                                                : tpl?.description ===
                                                  MINUTES_TEMPLATES_DILIGEN_ACADEMY_TEMPLATE_DESCRIPTION
                                                ? i18n.t(
                                                      'MINUTES_TEMPLATES_DILIGEN_ACADEMY_TEMPLATE_DESCRIPTION'
                                                  )
                                                : null}
                                        </Typography>
                                    )}
                                </Box>
                                {tpl?.isDefault && (
                                    <span className={classes.defaultTag}>
                                        {i18n.t('MINUTES_TEMPLATES_DEFAULT_TEMPLATE')}
                                    </span>
                                )}
                                {/* Preview Link */}
                                {showPreview && (
                                    <Typography
                                        className={classes.templateCardPreviewLink}
                                        onClick={() => {
                                            if (onPreviewButton) {
                                                onPreviewButton(tpl.id)
                                            } else {
                                                onPreview(tpl.id)
                                            }
                                        }}
                                        tabIndex={0}
                                        role="button"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault()
                                                if (withRadio) {
                                                    setPreviewSelection?.(tpl)
                                                    onPreview(tpl?.id)
                                                } else {
                                                    if (onPreviewButton) {
                                                        onPreviewButton(tpl.id)
                                                    } else {
                                                        onPreview(tpl.id)
                                                    }
                                                }
                                            }
                                        }}>
                                        {i18n.t('TEMPLATE_CARD_PREVIEW')}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </>
        )
    }

    const isChecked = (previewSelection?.id || templateOption?.id) === template?.id

    return (
        <Box
            className={`${classes.templateCard} ${
                withRadio && isChecked ? classes.templateCardSelected : ''
            }`}
            onClick={handleCardClick}
            style={{ cursor: withRadio ? 'pointer' : 'default' }}>
            {withRadio && (
                <ThemeProvider theme={theme}>
                    <Radio
                        value={template?.id}
                        inputProps={{ 'aria-label': template?.title }}
                        style={{ marginRight: 16 }}
                        tabIndex={0}
                        checked={(previewSelection?.id || templateOption?.id) === template?.id}
                        onChange={() => {
                            setPreviewSelection?.(template)
                        }}
                        onKeyDown={(e) => {
                            if (e.key === ' ' || e.key === 'Enter') {
                                e.preventDefault()
                                setPreviewSelection?.(template)
                                onPreview(template?.id)
                            }
                        }}
                    />
                </ThemeProvider>
            )}

            {/* Icon */}
            {template?.site === null && (
                <Box className={classes.logo}>
                    <DiligentBrandIcon />
                </Box>
            )}
            {/* Title and Description */}
            <Box className={classes.templateCardContent}>
                {isActionsOnly && (
                    <Typography className={classes.templateTitle}>
                        {i18n.t('MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE_ACTIONS_ONLY')}
                    </Typography>
                )}
                {!isActionsOnly && (
                    <Typography className={classes.templateTitle}>
                        {template?.title ===  MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE
                            ? i18n.t('MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE')
                            : template?.title ===
                                MINUTES_TEMPLATES_DILIGENT_ACADEMY_TEMPLATE_TITLE
                            ? i18n.t(
                                    'MINUTES_TEMPLATES_DILIGENT_ACADEMY_TEMPLATE_TITLE'
                                )
                            : truncateString(template?.title, 120)}
                    </Typography>
                )}
                {template?.description && (
                    <Typography className={classes.templateSubtitle}>
                        {isActionsOnly
                            ? i18n.t('MINUTES_TEMPLATES_ACTION_TEMPLATE_DESCRIPTION')
                            : template?.site === 
                            MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION
                                ? i18n.t(
                                      'MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION'
                                  )
                                : template?.description ===
                                  MINUTES_TEMPLATES_DILIGEN_ACADEMY_TEMPLATE_DESCRIPTION
                                ? i18n.t(
                                      'MINUTES_TEMPLATES_DILIGEN_ACADEMY_TEMPLATE_DESCRIPTION'
                                  )
                            : truncateString(template?.description, 120)}
                    </Typography>
                )}
            </Box>
            {template?.isDefault && !isActionsOnly && (
                <span className={classes.defaultTag}>
                    {i18n.t('MINUTES_TEMPLATES_DEFAULT_TEMPLATE')}
                </span>
            )}
            {/* Preview Link */}
            {showPreview && (
                <Typography
                    className={classes.templateCardPreviewLink}
                    onClick={handlePreviewClick}
                    tabIndex={0}
                    role="button"
                    onKeyDown={handleKeyDown}>
                    {i18n.t('TEMPLATE_CARD_PREVIEW')}
                </Typography>
            )}
        </Box>
    )
}

export default TemplateCard
